// The AI Designer's API calls, all in one module (docs/API.md «Chat», «Revisions and previews»,
// «Inspector edits and approve»: P4-B05, P4-B07, P4-B08).
import { queryOptions } from '@tanstack/react-query';
import { api, toApiError, unwrap, type ApiError, type Schemas } from '@/api/client';
import { ACTIVE, TERMINAL_EVENTS } from '@/api/jobs';
import { openStream } from '@/api/sse';
import type { Job } from '@/api/types';
import type { RevisionOut, VariantState } from '@/features/variants/api';

export type { RevisionOut, VariantState };
export type MessageOut = Schemas['ChatMessageOut'];
export type ToolChip = Schemas['ToolChip'];
export type ChatIn = Schemas['ChatIn'];
export type ChatStarted = Schemas['ChatStarted'];
export type PreviewOut = Schemas['PreviewOut'];
export type DiffOut = Schemas['DiffOut'];
export type ApprovedOut = Schemas['ApprovedOut'];
export type ThemeOut = Schemas['ThemeOut'];
export type ColorEdit = Schemas['ColorEdit'];

/** Pages are read whole (a variant's revisions and chat stay well under a page of 100). */
const ALL = { page: 1, size: 100 };

export const revisionsKey = (variantId: string) => ['variants', 'one', variantId, 'revisions'] as const;

/** GET /admin/variants/{id}/revisions: newest first. */
export const revisionsQuery = (variantId: string) =>
  queryOptions({
    queryKey: revisionsKey(variantId),
    queryFn: () => unwrap(api.GET('/admin/variants/{variant_id}/revisions', { params: { path: { variant_id: variantId }, query: ALL } })),
    select: (p): RevisionOut[] => p.items,
  });

/** POST /admin/variants/{id}/undo | redo: the head moves along the parent links. */
export function moveHead(variantId: string, dir: 'undo' | 'redo'): Promise<VariantState> {
  const params = { params: { path: { variant_id: variantId } } };
  return unwrap(dir === 'undo' ? api.POST('/admin/variants/{variant_id}/undo', params) : api.POST('/admin/variants/{variant_id}/redo', params));
}

/** GET /admin/revisions/{id}/preview-url: the revision's latest build on the preview origin (signed; null until one is ready). */
export const previewUrlQuery = (revisionId: string) =>
  queryOptions({
    queryKey: ['revisions', revisionId, 'preview-url'] as const,
    queryFn: (): Promise<PreviewOut> => unwrap(api.GET('/admin/revisions/{revision_id}/preview-url', { params: { path: { revision_id: revisionId } } })),
    staleTime: 60_000,
  });

/** POST /admin/variants/{id}/approve: freezes the revision (the head by default) and extracts `theme.json`. */
export function approveVariant(variantId: string, revisionId?: string | null): Promise<ApprovedOut> {
  return unwrap(api.POST('/admin/variants/{variant_id}/approve', { params: { path: { variant_id: variantId } }, body: { revision_id: revisionId ?? null } }));
}

/** GET /admin/websites/{id}/theme: the latest approved `theme.json` (Store Sync's source). */
export const themeQuery = (siteId: string) =>
  queryOptions({
    queryKey: ['websites', 'one', siteId, 'theme'] as const,
    queryFn: (): Promise<ThemeOut> => unwrap(api.GET('/admin/websites/{website_id}/theme', { params: { path: { website_id: siteId } } })),
    retry: false,
  });

// ── Chat (P4-B05) ──

export const messagesKey = (variantId: string) => ['variants', 'one', variantId, 'messages'] as const;

/** GET /admin/variants/{id}/messages: the whole conversation, oldest first (page after page of 100). */
export const messagesQuery = (variantId: string) =>
  queryOptions({
    queryKey: messagesKey(variantId),
    queryFn: async (): Promise<MessageOut[]> => {
      const out: MessageOut[] = [];
      for (let page = 1; ; page += 1) {
        const p = await unwrap(api.GET('/admin/variants/{variant_id}/messages', { params: { path: { variant_id: variantId }, query: { ...ALL, page } } }));
        out.push(...p.items);
        if (page >= p.pages) return out;
      }
    },
  });

/** POST /admin/variants/{id}/chat: 202 with the `ai.chat_edit` job and the stored PM message. */
export function sendChat(variantId: string, body: ChatIn): Promise<ChatStarted> {
  return unwrap(api.POST('/admin/variants/{variant_id}/chat', { params: { path: { variant_id: variantId } }, body }));
}

/** How a chat edit ended: its `result {revision, number, changed}` or the error it failed with. */
export type EditOutcome = { status: 'succeeded' | 'failed' | 'cancelled'; result: Record<string, unknown>; error: ApiError | null };

function outcomeOf(job: Pick<Job, 'status' | 'result' | 'retryable_error'>): EditOutcome {
  if (job.status === 'succeeded') return { status: 'succeeded', result: job.result ?? {}, error: null };
  const status = job.status === 'cancelled' ? 'cancelled' : 'failed';
  const body = job.retryable_error as { code?: string; params?: Record<string, unknown>; message?: string } | null;
  const code = body?.code ?? (status === 'cancelled' ? 'job_cancelled' : 'job_failed');
  return { status, result: {}, error: toApiError(409, { code, i18n_key: `errors.${code}`, params: body?.params ?? {}, message: body?.message ?? job.status, request_id: null }) };
}

/**
 * Follows a chat edit on `GET /admin/jobs/{id}/events` (contracts `sse-event` v2): `job.ai_tool` gives
 * each tool chip and `job.ai_text` the answer in chunks as the model works; the terminal event (or a
 * snapshot of a job that already ended) settles `done` with the job's outcome, read from
 * `GET /admin/jobs/{id}` (the terminal event carries `{status, error}`, not the result).
 */
export function followEdit(jobId: string, on: { tool: (chip: ToolChip) => void; text: (chunk: string) => void }): { done: Promise<EditOutcome>; close: () => void } {
  let stop: () => void = () => undefined;
  let settled = false;
  const done = new Promise<EditOutcome>((resolve) => {
    const finish = (fallback: Pick<Job, 'status' | 'result' | 'retryable_error'>) => {
      if (settled) return;
      settled = true;
      stop();
      unwrap(api.GET('/admin/jobs/{job_id}', { params: { path: { job_id: jobId } } })).then(
        (job) => resolve(outcomeOf(ACTIVE.has(job.status) ? fallback : job)),
        () => resolve(outcomeOf(fallback)),
      );
    };
    stop = openStream({
      path: `/admin/jobs/${jobId}/events`,
      isTerminal: (e) => TERMINAL_EVENTS.has(e.event),
      onEvent: (e) => {
        if (e.event === 'snapshot') {
          const job = (e.data as { job?: Job } | null)?.job;
          if (job && !ACTIVE.has(job.status)) finish(job);
          return;
        }
        const data = ((e.data as { data?: Record<string, unknown> } | null)?.data ?? {}) as Record<string, unknown>;
        const str = (v: unknown) => (typeof v === 'string' ? v : null);
        if (e.event === 'job.ai_text') on.text(str(data.text) ?? '');
        else if (e.event === 'job.ai_tool') on.tool({ name: str(data.name), target: str(data.target), result_summary: str(data.result_summary), error: data.error === true });
        else if (TERMINAL_EVENTS.has(e.event)) {
          const status = e.event === 'job.done' ? 'succeeded' : e.event === 'job.cancelled' ? 'cancelled' : 'failed';
          finish({ status, result: (data.result as Record<string, unknown> | undefined) ?? {}, retryable_error: (data.error as Job['retryable_error'] | undefined) ?? null });
        }
      },
    });
  });
  return {
    done,
    close: () => {
      settled = true;
      stop();
    },
  };
}

// ── Revisions (P4-B07) ──

/** POST /admin/revisions/{id}/restore: a new revision with that one's tree (revisions never change). */
export function restoreRevision(revisionId: string): Promise<VariantState> {
  return unwrap(api.POST('/admin/revisions/{revision_id}/restore', { params: { path: { revision_id: revisionId } } }));
}

/** POST /admin/revisions/{id}/checkpoint. */
export function checkpoint(revisionId: string, label: string): Promise<VariantState> {
  return unwrap(api.POST('/admin/revisions/{revision_id}/checkpoint', { params: { path: { revision_id: revisionId } }, body: { label } }));
}

/** GET /admin/revisions/{a}/diff/{b}: the files that differ, and both revisions' previews. */
export const diffQuery = (a: string, b: string) =>
  queryOptions({
    queryKey: ['revisions', a, 'diff', b] as const,
    queryFn: (): Promise<DiffOut> => unwrap(api.GET('/admin/revisions/{a}/diff/{b}', { params: { path: { a, b } } })),
    staleTime: Infinity,
  });

// ── Inspector edits (P4-B08): deterministic, without the model; one request is one revision ──

/** POST /admin/variants/{id}/inspector-edit: text, image or colour edits (the panel sends colour tokens). */
export function inspectorEdit(variantId: string, edits: ColorEdit[], baseRevisionId: string | null): Promise<VariantState> {
  return unwrap(
    api.POST('/admin/variants/{variant_id}/inspector-edit', { params: { path: { variant_id: variantId } }, body: { edits, base_revision_id: baseRevisionId } }),
  );
}

/** The approved research products the stand-in preview lists in «Популярное» (only approved ones, TZ §8). */
export const approvedProductsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ['research', 'items', orgId, { review_status: 'approved', kind: 'product', size: 4 }] as const,
    queryFn: () =>
      unwrap(api.GET('/admin/organizations/{org_id}/research-items', { params: { path: { org_id: orgId }, query: { review_status: 'approved', kind: 'product', size: 4 } } })),
    select: (p) => p.items,
  });
